<template>
  <div>
    <!-- Canvas -->
    <canvas ref="canvas" :style="canvasStyle"></canvas>

    <!-- Botón grande de descarga -->
    <div class="text-center mt-4" v-if="isDescargable">
      <v-btn block small color="primary" @click="downloadImage">
        Descargar
      </v-btn>
    </div>
  </div>
</template>

<script>
import QRCode from "qrcode";

export default {
  name: "VueQrCode",

  props: {
    config: {
      type: Object,
      required: true,
      default: () => ({
        styles: {
          width: 128,
          height: 128,
          colorDark: "#000000",
          colorLight: "#ffffff",
        },
        qr: {
          text: "",
          level: "M",
          margin: 4,
        },
        text: {
          content: "", // permite "\n"
          font: "16px sans-serif",
          color: "#000000",
          margin: 10, // espacio en px sobre el QR
        },
      }),
      validator: (obj) =>
        obj &&
        obj.styles &&
        obj.qr &&
        obj.text &&
        typeof obj.text.content === "string" &&
        typeof obj.text.font === "string" &&
        typeof obj.text.color === "string" &&
        typeof obj.text.margin === "number",
    },

    descargar: {
      type: Object,
      default: () => ({
        nombreArchivo: "Qr",
        extension: "jpeg",
      }),
      validator: (obj) =>
        obj &&
        typeof obj.nombreArchivo === "string" &&
        ["png", "jpeg"].includes(obj.extension),
    },
  },

  data() {
    return {
      downloadUrl: "",
    };
  },

  computed: {
    // tooltip + botón
    isDescargable() {
      return (
        this.descargar && ["png", "jpeg"].includes(this.descargar.extension)
      );
    },
    // estilos para canvas
    canvasStyle() {
      const { width, height, colorLight } = this.config.styles;
      const tieneTexto = this.config.text.content.trim() !== "";
      return {
        width: typeof width === "number" ? `${width}px` : width,
        height: tieneTexto
          ? "auto"
          : typeof height === "number"
          ? `${height}px`
          : height,
        backgroundColor: colorLight,
      };
    },
  },

  mounted() {
    this.generateQR();
  },

  methods: {
    // ajusta texto al ancho máximo del canvas
    wrapText(ctx, text, maxWidth) {
      // guardamos las lineas resultantes
      const lines = [];

      // separamos el texto en parrafos
      const paragraphs = text.split("\n");

      paragraphs.forEach((paragraph) => {
        // separamos el parrafo en palabras
        const words = paragraph.split(" ");
        // linea actual vacia
        let currentLine = "";

        words.forEach((word) => {
          // probamos a poner la palabra en la linea actual
          const testLine = currentLine ? currentLine + " " + word : word;
          const testWidth = ctx.measureText(testLine).width;

          if (testWidth <= maxWidth) {
            // si cabe, actualizamos la linea
            currentLine = testLine;
          } else {
            // si no cabe, guardamos la linea anterior
            if (currentLine) {
              lines.push(currentLine);
            }

            // comprobamos si la palabra entera cabe sola
            if (ctx.measureText(word).width <= maxWidth) {
              // si cabe, la ponemos en la nueva linea
              currentLine = word;
            } else {
              // si es muy larga, la partimos letra a letra
              let part = "";
              for (const char of word) {
                const testPart = part + char;
                if (ctx.measureText(testPart).width <= maxWidth) {
                  // si cabe, seguimos acumulando
                  part = testPart;
                } else {
                  // si ya no cabe, guardamos y empezamos otra parte
                  lines.push(part);
                  part = char;
                }
              }
              // usamos la ultima parte como nueva linea
              currentLine = part;
            }
          }
        });

        // al acabar el parrafo, guardamos lo que quede
        if (currentLine) {
          lines.push(currentLine);
        }
      });

      // devolvemos las lineas preparadas
      return lines;
    },

    generateQR() {
      const canvas = this.$refs.canvas;
      const { styles, qr, text } = this.config;

      // sacamos el ancho en pixeles
      let width = 128;
      if (styles.width === "auto") {
        width = canvas.parentElement.clientWidth;
      } else if (typeof styles.width === "number") {
        width = styles.width;
      } else {
        width = parseInt(styles.width, 10) || 128;
      }

      // hacemos un canvas temporal para el qr
      const canvaTemporal = document.createElement("canvas");
      const opt = {
        width: width,
        errorCorrectionLevel: qr.level,
        margin: qr.margin,
        color: { dark: styles.colorDark, light: styles.colorLight },
      };

      // generamos el qr en el temporal
      QRCode.toCanvas(canvaTemporal, qr.text, opt, (err) => {
        if (err) {
          console.error("error generando el qr", err);
          return;
        }

        // preparamos el texto
        const content = text.content.trim();
        const ctxTmp = canvaTemporal.getContext("2d");
        ctxTmp.font = text.font;

        // cortamos el texto en lineas
        const lines = content ? this.wrapText(ctxTmp, content, canvaTemporal.width) : [];
        const size = parseInt(text.font, 10) || 16;
        const lh = size * 1.2;
        const extra = lines.length ? text.margin + lh * lines.length : 0;

        // ajustamos el tamaño del canvas final
        canvas.width = canvaTemporal.width;
        canvas.height = canvaTemporal.height + extra;
        const ctx = canvas.getContext("2d");

        // pintamos fondo
        ctx.fillStyle = styles.colorLight;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // pintamos el qr
        ctx.drawImage(canvaTemporal, 0, 0); 

        // pintamos el texto si hay
        if (lines.length) {
          ctx.font = text.font;
          ctx.fillStyle = text.color;
          ctx.textAlign = "center";
          const cx = canvas.width / 2;
          lines.forEach((line, i) => {
            const y = canvaTemporal.height + text.margin + (i + 1) * lh - lh * 0.25;
            ctx.fillText(line, cx, y);
          });
        }

        // si queremos descargar, actualizamos enlace
        if (this.isDescargable) this.updateDownloadUrl();
      });
    },

    updateDownloadUrl() {
      const mime =
        this.descargar.extension === "jpeg" ? "image/jpeg" : "image/png";
      this.downloadUrl = this.$refs.canvas.toDataURL(mime);
    },

    downloadImage() {
      if (!this.downloadUrl) {
        this.updateDownloadUrl();
      }
      const link = document.createElement("a");
      link.href = this.downloadUrl;
      link.download = `${this.descargar.nombreArchivo}.${this.descargar.extension}`;
      link.click();
    },
  },
};
</script>
